import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Alert, CircularProgress, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { useApiClientOptions, useApiClients } from '../../../application/hooks/useApiClients';
import { ApiClientStatusChip } from './components/ApiClientStatusChip';
import { ConfidentialIcon } from './components/ConfidentialIcon';
import { DataTable } from '../../../../../shared/components/ui/DataTable';

function buildColumns(clientTypeLabels: Record<string, string>): GridColDef[] {
  return [
    {
      field: 'clientId',
      headerName: 'Client ID',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography
          sx={{
            fontSize: '13px',
            fontFamily: 'Courier Prime, monospace',
            color: '#0052ff',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Typography
          sx={{
            fontSize: '14px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            color: '#0b1c30',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'clientType',
      headerName: 'Type',
      width: 180,
      renderCell: (params) => (
        <Typography
          sx={{
            fontSize: '14px',
            fontFamily: 'Inter, sans-serif',
            color: '#434656',
          }}
        >
          {clientTypeLabels[params.value as string] ?? params.value}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <ApiClientStatusChip status={params.value as { key: string; value: string }} />
      ),
    },
    {
      field: 'isConfidential',
      headerName: 'Confidential',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <ConfidentialIcon isConfidential={params.value as boolean} />,
    },
  ];
}

/**
 * ApiClientListPage
 * Lista de API clients con diseño alineado a Stitch - Nexus Admin
 */
export const ApiClientListPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: clients = [], isLoading, error: queryError } = useApiClients();
  const { data: options } = useApiClientOptions();

  const clientTypeLabels: Record<string, string> = Object.fromEntries(
    (options?.clientTypes ?? []).map(({ value, label }) => [value, label]),
  );
  const columns = buildColumns(clientTypeLabels);

  const error =
    queryError instanceof Error ? queryError.message : queryError ? String(queryError) : null;

  const filteredClients = clients;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress sx={{ color: '#003ec7' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          sx={{
            mb: 2,
            backgroundColor: '#ffdad6',
            color: '#93000a',
            border: '1px solid #ffdad6',
          }}
        >
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{
            backgroundColor: '#003ec7',
            '&:hover': { backgroundColor: '#0052ff' },
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const handleRowClick = (params: GridRowParams) => {
    navigate(`/api-clients/${params.row.id}`);
  };

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        p: 4,
        backgroundColor: '#f8f9ff',
      }}
    >
      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography
            sx={{
              fontSize: '32px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              color: '#0b1c30',
              lineHeight: '40px',
              letterSpacing: '-0.02em',
            }}
          >
            API Clients
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontFamily: 'Inter, sans-serif',
              color: '#434656',
              mt: 0.5,
            }}
          >
            Manage and monitor authentication keys for external integrations.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          onClick={() => navigate('/api-clients/new')}
          sx={{
            backgroundColor: '#003ec7',
            color: '#ffffff',
            fontSize: '13px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '8px',
            px: 2,
            py: 1,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#0052ff',
              boxShadow: 'none',
            },
          }}
        >
          Create Client
        </Button>
      </Box>

      {/* Data Table */}
      <DataTable
        rows={filteredClients}
        columns={columns}
        emptyMessage="No API clients found"
        initialState={{
          pagination: { paginationModel: { pageSize: 10, page: 0 } },
          sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
        }}
        pageSizeOptions={[10, 25, 50]}
        onRowClick={handleRowClick}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />
    </Box>
  );
};
